import { Match, Show, Switch, useContext } from 'solid-js';
import DynamicModuleGenerator from '../../dynamic-module-generator';
import { Button } from '../../ui-components/button/button';
import { ImageComponent } from '../image-component/image-component';
import { FaqItemType } from './faq-item';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { VideoIframeContainer } from '../../ui-components/video-iframe-container/video-iframe-container';

type FaqContentModalProps = {
    faqItem: FaqItemType;
    onClose: () => void;
    setLastTabbableElement: (el?: any) => void;
};

/**
 * Category + question, passed to `Modal`'s `header` slot so it stays put while the
 * answer scrolls. Horizontal padding comes from the modal shell, which keeps this
 * flush with the body below it.
 */
export const FaqContentModalHeader = (props: { faqItem: FaqItemType }) => (
    <div class="mb-cvi-9 flex flex-col gap-cvi-2 text-left">
        <span class="body-xs font-cvi-medium text-cvi-fg-default">
            {props.faqItem.category}
        </span>
        <h3 class="heading-2xl font-cvi-medium m-0 text-cvi-fg-default">
            {props.faqItem.question}
        </h3>
    </div>
);

export const FaqContentModal = (props: FaqContentModalProps) => {
    const { localize } = useContext(AppContext);

    return (
        <div class="text-left">
            {/* Media leads on a narrow viewport (flex-col-reverse), sits beside the
                copy from 920px up. */}
            <div class="flex gap-cvi-13 text-left max-tablet-original:flex-col-reverse max-tablet-original:gap-cvi-8">
                <div class="min-w-0 flex-1">
                    <DynamicModuleGenerator
                        content={props.faqItem.blocks}
                        skipSectionConversion={true}
                    />
                </div>

                <Switch>
                    <Match when={props.faqItem.mediaType === 'video' && props.faqItem?.video?.video_link}>
                        <div class="min-w-0 flex-1 overflow-hidden rounded-cvi-md">
                            <VideoIframeContainer videoLink={props.faqItem.video?.video_link} />
                        </div>
                    </Match>
                    <Match when={props.faqItem.mediaType === 'image' && props.faqItem?.image?.url}>
                        <div class="min-w-0 flex-1 overflow-hidden rounded-cvi-md">
                            <ImageComponent
                                src={props.faqItem!.image.url}
                                height={'AUTO'}
                                width={500}
                                altText={props.faqItem!.image.alt || ''}
                                {...props.faqItem!.image.alt
                                    ? {}
                                    : { role: 'presentation' }
                                }
                                aspectRatio={'original'}
                                ratio={props.faqItem!.image.width / props.faqItem!.image.height}
                            />
                        </div>
                    </Match>
                </Switch>

            </div>

            {/* Supplementary links as DS pill buttons (Storybook `components-button-button`):
                the first is the primary action, a second one steps down to secondary-neutral. */}
            <div class="mt-cvi-9 flex flex-wrap items-center gap-cvi-4">
                <Show when={props.faqItem.link1?.url}>
                    <Button
                        label={props.faqItem.link1?.title || ''}
                        url={props.faqItem!.link1!.url || ''}
                        variant='primary'
                        isLink={true}
                        noBlockSpacing
                        element={(el: HTMLElement) => {
                            // If there is no second link, set the last tabbable element to the first link
                            if (!props.faqItem.link2?.url) {
                                props.setLastTabbableElement(el);
                            }
                        }}
                    />
                </Show>
                <Show when={props.faqItem.link2?.url}>
                    <Button
                        label={props.faqItem.link2?.title || ''}
                        url={props.faqItem!.link2!.url || ''}
                        variant='secondary-neutral'
                        isLink={true}
                        noBlockSpacing
                        element={(el: HTMLElement) => props.setLastTabbableElement(el)}
                    />
                </Show>
                <Show when={!(props.faqItem.link1?.url || props.faqItem.link2?.url)}>
                    {/*
                        If there are no supplementary links, we need this placeholder button to set the last tabbable element.
                        Otherwise the last tab would take the user out of the modal.
                    */}
                    <div class="ml-auto">
                        <Button
                            element={(el: HTMLElement) => props.setLastTabbableElement(el)}
                            label={localize('close', 'Close')}
                            onClick={props.onClose}
                            variant='primary'
                            noBlockSpacing
                        />
                    </div>
                </Show>
            </div>

        </div>
    );
};
